import { takeLatest, select, put, take, call, race } from 'redux-saga/effects';
import size from 'lodash/size';

import * as acceptFamilyInvite from '../../circles/acceptInviteToFamilyCircle';
import * as declineFamilyInvite from '../../circles/declineInviteToFamilyCircle';
import * as acceptCareInvite from '../../circles/acceptCaregiverRole';
import * as declineCareInvite from '../../circles/declineCaregiverRole';
import * as acceptGuardianInvite from '../../circles/accept-guardian-role';
import * as declineGuardianInvite from '../../circles/decline-guardian-role';
import * as upcomingSurveyCompletions from '../../surveys/upcoming-survey-completions';
import * as scheduledTasks from '../requests/scheduled-tasks-upcoming-req';
import * as scheduledCompletedTasks from '../requests/scheduled-tasks-completed-req';
import * as competeTaskCustom from '../requests/complete-task-custom-req';
import * as competeTask from '../requests/complete-task-req';
import * as completeCarePlanTask from '../requests/complete-care-plan-task-req';

import {
  selectScheduledTasks,
  selectScheduledTasksCount,
  selectScheduledCompletedTasksCount,
} from '../selectors';
import { HEALTHBOARD_HOME } from '../../../constants/routes';
import * as acceptedConsent from '../requests/sign-appointment-consent-req';
import * as getLoggedUser from '../../profiles/get-logged-user';
import { currentLocationSelector } from '../../common/selectors';
import {
  loggedUserIdSelector,
  targetUserIdSelector,
} from '../../context/selectors';
import { TODO_TYPES } from '../../../constants';
import { activeTodoActions } from '../activeTodo';
import * as errorUI from '../../ui/error';

const FIRST_REQUEST_LIMIT = 20;

const ACTIONS_MAP = {
  // family
  [acceptFamilyInvite.consts.SEND]: acceptFamilyInvite.consts.SUCCEED,
  [declineFamilyInvite.consts.SEND]: declineFamilyInvite.consts.SUCCEED,
  // caregiver
  [acceptCareInvite.consts.SEND]: acceptCareInvite.consts.SUCCEED,
  [declineCareInvite.consts.SEND]: declineCareInvite.consts.SUCCEED,
  // guardian
  [acceptGuardianInvite.consts.SEND]: acceptGuardianInvite.consts.SUCCEED,
  [declineGuardianInvite.consts.SEND]: declineGuardianInvite.consts.SUCCEED,
};

function* loadOneScheduleTask() {
  const location = yield select(currentLocationSelector);
  const id = yield select(targetUserIdSelector);
  const isHomeLocation = location.includes(`/${id}/${HEALTHBOARD_HOME}`);
  const counter = yield select(selectScheduledTasksCount);
  const list = yield select(selectScheduledTasks);

  if (counter >= FIRST_REQUEST_LIMIT || isHomeLocation) {
    yield put(
      scheduledTasks.paginationActions.send({
        limit: 1,
        offset: size(list),
        id,
      }),
    );
  }
}

function* fetchCompletedTodo() {
  const id = yield select(targetUserIdSelector);
  const count = yield select(selectScheduledCompletedTasksCount);

  yield put(
    scheduledCompletedTasks.actions.send({
      limit: FIRST_REQUEST_LIMIT,
      offset: 0,
      id,
      silent: true,
      count,
    }),
  );
}

// Invitation

function* handleInvitationSaga({ type, payload: { id } }) {
  const succeedAction = ACTIONS_MAP[type];
  yield take(succeedAction);
  yield put(
    scheduledTasks.actions.removeEntity({
      type: TODO_TYPES.INVITATION,
      id,
    }),
  );
  yield call(loadOneScheduleTask);
}

// Survey

function* removeCompletedSurvey(assignmentId) {
  yield call(fetchCompletedTodo);
  yield put(
    scheduledTasks.actions.removeEntity({
      type: TODO_TYPES.SURVEY,
      matchParam: 'assignmentId',
      id: assignmentId,
      keepCount: true,
    }),
  );
  yield call(loadOneScheduleTask);
}

function* toggleSurveyVisibility(id, visible) {
  yield put(
    scheduledTasks.actions.toggleVisibility({
      type: TODO_TYPES.SURVEY,
      matchParam: 'assignmentId',
      id,
      visible,
    }),
  );
}

function* handleStartCompletion({ payload: { assignmentId } }) {
  yield call(toggleSurveyVisibility, assignmentId, false);
  yield put(activeTodoActions.setActiveItem(null));

  const { succeed } = yield race({
    succeed: take(upcomingSurveyCompletions.consts.SUCCEED),
    failed: take([
      upcomingSurveyCompletions.consts.REJECTED,
      upcomingSurveyCompletions.consts.ERROR_ENCOUNTERED,
    ]),
  });

  if (succeed) {
    yield call(removeCompletedSurvey, assignmentId);
  } else {
    yield call(toggleSurveyVisibility, assignmentId, true);
    yield put(errorUI.actions.update({ id: 'errors.common' }));
  }
}

// Task

function* handleTaskSaga({ payload }) {
  const { status, id, outcomeId, attachments, attachmentRequired } = payload;

  const formData = new FormData();
  formData.append('status', status);
  formData.append('id', id);

  if (outcomeId) {
    formData.append('chosen_outcome', outcomeId);
  }

  if (size(attachments) && attachmentRequired) {
    attachments.map(item => {
      return formData.append(`outcome_attachments[]`, item.file);
    });
  }

  yield put(competeTask.actions.send(formData));

  const { failed, succeed } = yield race({
    succeed: take(competeTask.consts.SUCCEED),
    failed: take(competeTask.consts.ERROR_ENCOUNTERED),
  });

  if (failed) {
    const textId = attachmentRequired
      ? 'task.error.uploadFailed'
      : 'errors.common';
    yield put(errorUI.actions.update({ id: textId }));
  }

  if (succeed) {
    yield call(fetchCompletedTodo);
    yield put(
      scheduledTasks.actions.removeEntity({
        type: TODO_TYPES.TASK,
        matchParam: 'taskAssignmentId',
        id,
      }),
    );
    yield call(loadOneScheduleTask);
  }
}

function* handleTaskCustomSaga({ payload }) {
  const { id, status, userId } = payload;

  const formData = new FormData();
  formData.append('id', id);
  formData.append('status', status);
  formData.append('user_id', userId);

  yield put(competeTaskCustom.actions.send(formData));

  const { failed, succeed } = yield race({
    succeed: take(competeTaskCustom.consts.SUCCEED),
    failed: take(competeTaskCustom.consts.ERROR_ENCOUNTERED),
  });

  if (failed) {
    yield put(errorUI.actions.update({ id: 'errors.common' }));
  }

  if (succeed) {
    yield call(fetchCompletedTodo);
    yield put(
      scheduledTasks.actions.removeEntity({
        type: TODO_TYPES.TASK_CUSTOM,
        matchParam: 'id',
        id,
      }),
    );
    yield call(loadOneScheduleTask);
  }
}

function* handleCarePlanTaskSaga({ payload }) {
  const { id, itemId, userId, outcomeId, attachments } = payload;

  const formData = new FormData();
  formData.append('task_id', itemId);
  formData.append('user_id', userId);
  formData.append('chosen_outcome', outcomeId);

  attachments.forEach(({ file }) =>
    formData.append('outcome_attachments[]', file),
  );

  yield put(completeCarePlanTask.actions.send(formData));

  const { failed, succeed } = yield race({
    succeed: take(completeCarePlanTask.consts.SUCCEED),
    failed: take(completeCarePlanTask.consts.ERROR_ENCOUNTERED),
  });

  if (failed) {
    yield put(errorUI.actions.update({ id: 'errors.common' }));
  }

  if (succeed) {
    yield call(fetchCompletedTodo);
    yield put(
      scheduledTasks.actions.removeEntity({
        type: TODO_TYPES.CARE_PLAN_TASK,
        matchParam: 'id',
        id,
      }),
    );
    yield call(loadOneScheduleTask);
  }
}

// Consent
function* handleAcceptedConsent() {
  const userId = yield select(loggedUserIdSelector);
  yield call(fetchCompletedTodo);
  yield call(loadOneScheduleTask);
  yield put(getLoggedUser.actions.send({ id: userId }));
}

export default function* manageTodoSaga() {
  const actionsToListen = Object.keys(ACTIONS_MAP);
  yield takeLatest([actionsToListen], handleInvitationSaga);
  yield takeLatest(
    [upcomingSurveyCompletions.consts.SEND],
    handleStartCompletion,
  );
  yield takeLatest([competeTask.consts.START], handleTaskSaga);
  yield takeLatest([competeTaskCustom.consts.START], handleTaskCustomSaga);
  yield takeLatest(acceptedConsent.actions.succeed, handleAcceptedConsent);
  yield takeLatest([completeCarePlanTask.consts.START], handleCarePlanTaskSaga);
}



// WEBPACK FOOTER //
// ./src/redux/healthboard/sagas/manage-todo-saga.js